import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { ShieldCheck, Star, Sparkles, PenLine, Palette } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../state/auth';
import { useCart } from '../state/cart';
import { getStats } from '../data/reviews';
import BookViewer, { buildBackPage, buildDedicationPage, buildFullBookPages } from '../components/BookViewer';
import BookDownload from '../components/BookDownload';
import StoryLoader from '../components/StoryLoader';
import FinalizeLoader from '../components/FinalizeLoader';
import PreviewHelpModal from '../components/PreviewHelpModal';
import { trackEvent } from '../lib/track';
import { useT, useLang, useLocalField } from '../i18n';

// While a story is still generating it sits at 'previewing'; the create flow
// navigates here straight away and we poll until it flips to 'preview_ready'
// (or 'failed'). Cap the wait so a stuck/failed job eventually surfaces an
// error instead of spinning forever.
const POLL_INTERVAL_MS = 2500;
// Generation runs server-side and reliably finishes even when it's slow (a
// cover with outpaint retries + a scene-0 image that can take up to ~3 min on
// Seedream). The old 4-min cap let the client give up and show "failed" while
// the server was still working — then the story appeared on reload. 7 min keeps
// the client waiting through the realistic worst case instead of false-failing.
const MAX_POLL_MS = 7 * 60 * 1000;
// How many times a 'failed' state silently RE-GENERATES the story (server-side,
// from the persisted inputs snapshot) before finally surfacing the error UI with
// a manual "try again" button. A plain reload can never clear a real failure —
// the story is already marked 'failed' in the DB, so re-fetching just re-reads
// it. Only re-running the pipeline can recover it. Kept at 1 because a regen is a
// full (expensive) pipeline run; the manual button covers further attempts.
const MAX_AUTO_REGEN = 1;

// Preview-cap layout (enforced server-side by previewMode in storyPipeline):
//   • 1 scene with image+text  → the one scene the pipeline actually illustrates
//   • 3 scenes as text-only    → the next teaser scenes, no image generated
//   • remaining scenes locked  → blurred placeholder + lock overlay
const TEXT_ONLY_TEASER_COUNT = 3;

// ── "Need a hand?" hesitation detector ───────────────────────────────────────
// A parent who is unsure about the AI draft tends to flip quickly back-and-forth
// between pages and scroll restlessly in the first moments — instead of reading
// forward calmly. When we spot that early we surface a warm offer to hand them to
// a human on WhatsApp who can make the pictures print-ready, before they bounce.
// Shown at most once per book per browser, never after they head to checkout.
const HELP_MIN_MS = 3500;          // don't interrupt the first few seconds
const HELP_EARLY_WINDOW_MS = 90000; // only EARLY hesitation counts; later = engaged, leave them be
const HELP_SCORE_TO_OPEN = 5;      // hesitation points that trigger the offer
const HELP_QUICK_GAP_MS = 4000;    // two actions closer than this read as restless
// A back-and-forth page reversal is the clearest "I'm unsure" tell, so it's
// worth more than a plain forward flip or a scroll nudge.
const HELP_PTS_REVERSAL = 3;
const HELP_PTS_FLIP = 1;
const HELP_PTS_SCROLL = 1;

function buildPreviewPages(story, t) {
  const pages = [];
  pages.push({
    kind: 'front',
    title: story.title,
    imageUrl: story.coverImageUrl || null,
    titleTheme: story.coverTitleTheme === 'light' ? 'light' : 'dark',
    titleColors: story.coverTitleColors || null,
    titleStyle: story.coverTitleStyle || null,
    titlePosition: story.coverTitlePosition || null,
  });

  let textOnlyShown = 0;
  (story.scenes || []).forEach((s, i) => {
    if (s.imageUrl) {
      pages.push({ kind: 'scene', text: s.text, imageUrl: s.imageUrl, sceneIndex: i });
    } else if (textOnlyShown < TEXT_ONLY_TEASER_COUNT) {
      pages.push({ kind: 'text-only', text: s.text, sceneIndex: i });
      textOnlyShown++;
    } else {
      pages.push({ kind: 'locked' });
    }
  });

  // The keepsake dedication page (last page inside) and the back cover always
  // close the book — even in preview. Part of the storybook metaphor, no
  // spoilers (summary + gendered dedication derived from personalization).
  pages.push(buildDedicationPage(story, t));
  pages.push(buildBackPage(story, t));

  return pages;
}

export default function Preview() {
  const t = useT();
  const { lang } = useLang();
  const lf = useLocalField();
  const STATS = useMemo(() => getStats(lang), [lang]);
  const { shareToken } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const { saveGuestItem } = useCart();
  const [data, setData] = useState(null);
  // We only tolerate a 404 (the stub not existing yet) while polling when we
  // just came from the create form — that's the only moment the create request
  // and this page can race. On a directly-opened link a 404 is a bad token, so
  // we fail fast instead of spinning. It also seeds the initial 'generating'
  // status so the loader carries over from the create page with no flash of the
  // plain spinner.
  const cameFromCreate = !!location.state?.generating;
  // status: 'loading' (first fetch) | 'generating' (still being created, keep
  // polling) | 'ready' (render the book) | 'failed' (failed or gave up) |
  // 'notfound' (bad/unknown token).
  const [status, setStatus] = useState(cameFromCreate ? 'generating' : 'loading');
  // Paid-book finalize step: a paid story may still have unillustrated scenes
  // (the preview only renders cover + scene 0; the rest fill in post-payment).
  // When a paid buyer opens this page we top up the missing scenes — gated
  // server-side on the paid order — so the public book page shows the whole book.
  const [finalizing, setFinalizing] = useState(false);
  // True during the short window between giving up and an automatic retry, so we
  // keep showing the loader instead of flashing the error screen.
  const [autoReloading, setAutoReloading] = useState(false);
  // Bumped each time we kick off a server-side regenerate; the poll effect lists
  // it as a dependency, so incrementing it restarts polling with a fresh deadline
  // to pick up the freshly re-run story.
  const [regenNonce, setRegenNonce] = useState(0);
  // Guards against firing a second regenerate while one is already in flight
  // (e.g. the auto-retry effect and an impatient manual tap landing together).
  const regenInFlight = useRef(false);

  // "Need a hand?" hesitation offer (unpaid preview only). helpReadyAt marks when
  // the ready book first rendered; the rest accumulate the hesitation score from
  // page flips + scrolls until it crosses the threshold (see constants above).
  const [helpOpen, setHelpOpen] = useState(false);
  const helpReadyAt = useRef(0);
  const helpScore = useRef(0);
  const helpLastNavDir = useRef(null);
  const helpLastActionAt = useRef(0);
  const helpShown = useRef(false);   // opened once already this mount
  const buyClicked = useRef(false);  // headed to checkout → stop offering
  const helpKey = `iamhero_preview_help:${shareToken}`;

  useEffect(() => {
    let cancelled = false;
    let settled = false; // reached a terminal state (ready/failed/notfound)
    let timer = null;
    const startedAt = Date.now();
    const expired = () => Date.now() - startedAt >= MAX_POLL_MS;

    const finish = (s, d) => {
      settled = true;
      if (d !== undefined) setData(d);
      setStatus(s);
    };

    // The story keeps generating server-side regardless of what happens on the
    // client, so a SINGLE failed poll must never end the loop — on mobile a poll
    // routinely fails for reasons unrelated to the story: a dropped fetch on a
    // flaky network, the screen locking and aborting the in-flight request, or a
    // Cloud Run cold-start 5xx. Only an explicit server 'failed', a confirmed
    // bad token, or a real timeout (confirmed by a fresh fetch) should fail.
    const poll = async () => {
      let d;
      try {
        d = await api.get(`/api/stories/preview/${shareToken}`);
      } catch (err) {
        if (cancelled) return;
        if (err.status === 404) {
          // A create request that reaches the server writes the story stub
          // immediately, so a 404 means it has not landed (yet). If the create
          // POST is known to have hard-failed (server down/redeploying — it never
          // creates the stub), fail fast instead of spinning to the deadline.
          let createFailed = false;
          try { createFailed = sessionStorage.getItem(`iamhero_create_failed:${shareToken}`) === '1'; } catch { /* private mode */ }
          if (createFailed) { finish('failed'); return; }
          // A directly-opened link with no create in flight is a bad token.
          if (!cameFromCreate) { finish('notfound'); return; }
          // Otherwise it's the normal create→preview race (incl. slow photo
          // uploads before the stub is written): fall through and keep polling.
        }
        // The create-race 404 and every transient error (network drop, 5xx cold
        // start, background-aborted fetch, empty response from a restart) retry
        // until the deadline rather than failing on a single bad poll.
        if (expired()) finish('failed');
        else { setStatus('generating'); timer = setTimeout(poll, POLL_INTERVAL_MS); }
        return;
      }
      if (cancelled) return;
      if (d.generationStatus === 'failed') {
        finish('failed');
      } else if (d.generationStatus === 'previewing') {
        // Only give up once a FRESH fetch still shows 'previewing' past the
        // deadline — the fetch above is itself the final confirmation, so a
        // story that just finished is never reported as a failure.
        if (expired()) finish('failed');
        else { setStatus('generating'); timer = setTimeout(poll, POLL_INTERVAL_MS); }
      } else {
        finish('ready', d);
      }
    };

    // iOS Safari suspends setTimeout while the tab is backgrounded (screen lock,
    // app switch), so the loop can stall. Re-poll the instant the page returns
    // to the foreground so a story that finished while backgrounded shows up
    // immediately instead of waiting on a stalled timer.
    const onVisible = () => {
      if (cancelled || settled || document.visibilityState !== 'visible') return;
      if (timer) { clearTimeout(timer); timer = null; }
      poll();
    };
    document.addEventListener('visibilitychange', onVisible);

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [shareToken, cameFromCreate, regenNonce]);

  // A paid book that still has unillustrated scenes: top them up exactly like the
  // finalize page does (the post-payment delivery job usually does this already;
  // this is the resilient fallback if the buyer arrives first or that job failed).
  // Gated server-side on the paid order, keyed by shareToken — no auth needed.
  useEffect(() => {
    if (status !== 'ready' || !data?.paid) return;
    // Manual (hand-built) books are completed by the admin, not the pipeline —
    // their image-less scenes are intentional (locked preview pages), and the
    // server finalize is a no-op for them. Skip the top-up so we don't loop
    // calling finalize on a book the pipeline can never fill.
    if (data.isManual) return;
    const needsFinalize = (data.scenes || []).some((s) => !s.imageUrl);
    if (!needsFinalize) return;
    let cancelled = false;
    (async () => {
      setFinalizing(true);
      try {
        await api.post(`/api/stories/${shareToken}/finalize-illustrations`, {});
        const refreshed = await api.get(`/api/stories/preview/${shareToken}`);
        if (!cancelled) setData(refreshed);
      } catch (err) {
        // Leave whatever scenes we have; the book page still renders + downloads.
        console.error('[preview] finalize failed:', err.message);
      } finally {
        if (!cancelled) setFinalizing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [status, data, shareToken]);

  const pages = useMemo(
    () => (data ? (data.paid ? buildFullBookPages(data, t) : buildPreviewPages(data, t)) : []),
    [data, t],
  );

  // Guests have no server-side cart (that needs auth), so when a guest reaches a
  // ready preview, add the item to the local guest cart — mirroring how a
  // logged-in user's story is auto-added at generation. Keyed by shareToken, so
  // it's idempotent and survives navigation/reload until they pay or remove it.
  useEffect(() => {
    if (status !== 'ready' || !data || user) return;
    const tmpl = data.template;
    const sku = data.personalization?.variantSku;
    const v = tmpl?.variants?.find((x) => x.sku === sku)
      || tmpl?.variants?.find((x) => x.isActive);
    saveGuestItem({
      shareToken,
      nameAr: tmpl?.nameAr,
      nameHe: tmpl?.nameHe,
      variantSku: v?.sku,
      variantType: v?.type,
      priceCents: v?.priceCents,
      personalization: { name: data.personalization?.name },
    });
  }, [status, data, user, shareToken, saveGuestItem]);

  // Ask the server to re-run generation for a failed story, then restart polling
  // (via regenNonce) so we pick up the new result. Keeps the loader up the whole
  // time so the error screen never flashes mid-retry. Falls back to surfacing the
  // error UI when the story can't be regenerated server-side (legacy story with
  // no inputs snapshot → 409) or the request itself errors.
  const triggerRegen = useCallback(async () => {
    if (regenInFlight.current) return;
    regenInFlight.current = true;
    setAutoReloading(true); // hold the loader while the regen request is in flight
    try {
      await api.post(`/api/stories/${shareToken}/regenerate`, {});
    } catch {
      // 409 (not regenerable) or a network error: nothing more we can do
      // automatically — show the error screen with the manual button.
      regenInFlight.current = false;
      setAutoReloading(false);
      setStatus('failed');
      return;
    }
    regenInFlight.current = false;
    setStatus('generating');
    setRegenNonce((n) => n + 1); // restart the poll loop with a fresh deadline
  }, [shareToken]);

  // When the poll loop gives up, don't dead-end on the error screen — silently
  // re-run generation up to MAX_AUTO_REGEN times first. Unlike a reload, this
  // actually re-runs the pipeline, so a transient blip (an empty LLM response, a
  // one-off provider hiccup) recovers on its own. The counter lives in
  // sessionStorage so it survives reloads, and is cleared on success.
  const regenKey = `iamhero_preview_regens:${shareToken}`;
  useEffect(() => {
    if (status === 'ready') {
      try { sessionStorage.removeItem(regenKey); } catch { /* private mode */ }
      return;
    }
    if (status !== 'failed') return;
    let count = 0;
    try { count = parseInt(sessionStorage.getItem(regenKey) || '0', 10) || 0; } catch { /* private mode */ }
    if (count < MAX_AUTO_REGEN) {
      try { sessionStorage.setItem(regenKey, String(count + 1)); } catch { /* private mode */ }
      triggerRegen();
    }
  }, [status, regenKey, triggerRegen]);

  // Manual "try again" — re-runs generation server-side (the in-flight guard
  // prevents overlap with an auto-retry). NOT bounded by the auto-regen counter,
  // so the user can keep retrying a stubbornly-failing story by hand.
  const handleRetry = () => { triggerRegen(); };

  // Buy-funnel analytics. Once per shareToken per session so reloads/re-polls
  // don't inflate the counts. `preview_failed_screen` only fires when the error
  // card will actually persist (auto-regens exhausted) — a silently-recovered
  // failure never shows the user anything, so it isn't a funnel drop.
  useEffect(() => {
    if (status !== 'ready' && status !== 'failed') return;
    if (status === 'failed') {
      let count = 0;
      try { count = parseInt(sessionStorage.getItem(regenKey) || '0', 10) || 0; } catch { /* private mode */ }
      if (count < MAX_AUTO_REGEN) return; // will auto-regen, no error screen shown
    }
    if (status === 'ready' && data?.paid) return; // post-purchase book view, not funnel
    const evt = status === 'ready' ? 'preview_ready' : 'preview_failed_screen';
    const seenKey = `iamhero_evt:${evt}:${shareToken}`;
    try {
      if (sessionStorage.getItem(seenKey)) return;
      sessionStorage.setItem(seenKey, '1');
    } catch { /* private mode — accept possible dupes */ }
    trackEvent(evt, { slug: shareToken, name: data?.template?.nameAr });
  }, [status, data, shareToken, regenKey]);

  const trackBuyClick = () => {
    // Once a visitor heads to checkout they're no longer wavering — suppress the
    // hesitation offer (and close it if it happens to be open).
    buyClicked.current = true;
    setHelpOpen(false);
    trackEvent('buy_click', { slug: shareToken, name: data?.template?.nameAr });
  };

  // Add hesitation points; open the offer once the score crosses the threshold
  // inside the early window. Ignored after the visitor bought, after we've shown
  // it once, or on a paid book. Persisted per book so a reload doesn't re-nag.
  const bumpHesitation = useCallback((points) => {
    if (helpShown.current || buyClicked.current || helpOpen) return;
    if (!data || data.paid) return;
    const startedAt = helpReadyAt.current;
    if (!startedAt) return;
    const elapsed = Date.now() - startedAt;
    if (elapsed < HELP_MIN_MS || elapsed > HELP_EARLY_WINDOW_MS) return;
    helpScore.current += points;
    if (helpScore.current < HELP_SCORE_TO_OPEN) return;
    helpShown.current = true;
    try { localStorage.setItem(helpKey, '1'); } catch { /* private mode */ }
    setHelpOpen(true);
    trackEvent('preview_help_shown', { slug: shareToken, name: data?.template?.nameAr });
  }, [data, helpOpen, helpKey, shareToken]);

  // Each page flip in the viewer. A direction reversal (forward then back, or the
  // reverse) is the strongest indecision signal; a flip that lands quickly after
  // the previous action reads as restless skimming and earns the reversal weight.
  const handlePreviewNav = useCallback((dir) => {
    const now = Date.now();
    const reversal = helpLastNavDir.current && helpLastNavDir.current !== dir;
    const quick = helpLastActionAt.current && now - helpLastActionAt.current < HELP_QUICK_GAP_MS;
    helpLastNavDir.current = dir;
    helpLastActionAt.current = now;
    bumpHesitation(reversal || quick ? HELP_PTS_REVERSAL : HELP_PTS_FLIP);
  }, [bumpHesitation]);

  // Restless early scrolling is a second, independent hesitation signal. We only
  // count sizeable jumps and throttle them so a single fling isn't scored twice.
  useEffect(() => {
    if (status !== 'ready' || !data || data.paid) return;
    // Already offered for this book on this device — don't re-nag on reload.
    try { if (localStorage.getItem(helpKey) === '1') { helpShown.current = true; return; } } catch { /* private mode */ }
    helpReadyAt.current = Date.now();
    let lastY = window.scrollY || 0;
    let lastBump = 0;
    const onScroll = () => {
      const y = window.scrollY || 0;
      if (Math.abs(y - lastY) < 220) return;
      lastY = y;
      const now = Date.now();
      if (now - lastBump < 600) return;
      lastBump = now;
      bumpHesitation(HELP_PTS_SCROLL);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [status, data, helpKey, bumpHesitation]);

  // Persist where the customer dragged the cover title so it sticks on reload
  // and flows into the book/PDF they buy. Optimistically fold the new position
  // into local data too, so a page-flip re-renders the cover in place (no flash
  // back to the old spot) even before the request returns.
  const handleTitleMove = useCallback(async (position) => {
    setData((prev) => (prev ? { ...prev, coverTitlePosition: position } : prev));
    try {
      await api.patch(`/api/stories/${shareToken}/title-position`, { position });
    } catch (err) {
      // Non-fatal: the on-screen title still shows where they dropped it.
      console.error('[preview] save title position failed:', err.message);
    }
  }, [shareToken]);

  // Keep the friendly loader up during the brief pre-reload window so the user
  // never sees the error flash before an automatic retry.
  if (status === 'generating' || autoReloading) return <StoryLoader />;
  // Topping up a paid book's remaining illustrations — show the rich finalize
  // loader (the generic spinner reads as "broken" during a 1-2 min wait).
  if (finalizing) return <FinalizeLoader />;
  if (status === 'loading') return <div className="loader"><div className="loader-spinner" /></div>;
  if (status === 'failed') return (
    <div className="error-card">
      <h2>{t.previewFailed}</h2>
      <div className="error-card-actions">
        <button type="button" className="submit-btn" onClick={handleRetry}>{t.tryAgain}</button>
        <Link to="/" className="ghost-btn">{t.backToHome}</Link>
      </div>
    </div>
  );
  if (status === 'notfound' || !data) return (
    <div className="error-card">
      <h2>{t.templateNotFound}</h2>
      <Link to="/" className="submit-btn">{t.backToHome}</Link>
    </div>
  );

  const { template, personalization } = data;
  const variantSku = personalization?.variantSku;
  const variant = template?.variants?.find(v => v.sku === variantSku)
    || template?.variants?.find(v => v.isActive);

  // Jump to the associated order when one exists. The order page is auth-gated
  // (owner/admin), so only offer it to a logged-in user — admins viewing a
  // shared preview, or the buyer returning to their order.
  const orderLink = (user && data.orderId) ? (
    <Link to={`/account/orders/${data.orderId}`} className="ghost-btn preview-order-link">
      ← {t.previewGoToOrder}
    </Link>
  ) : null;

  // Paid book page: the buyer (opening the shareToken link from the post-payment
  // email) gets the finished book presented nicely + a PDF download — no checkout
  // CTA and no printing/order copy. `includesPdf` gates the download so a
  // physical-only buyer doesn't get a free PDF they didn't pay for.
  if (data.paid) {
    return (
      <div className="preview-page preview-page--paid">
        <div className="preview-header">
          {lf(template || {}, 'nameAr') && (
            <p className="preview-template">{t.storyLineLabel}: {lf(template, 'nameAr')}</p>
          )}
          <h1 className="book-ready-title">{t.bookReadyTitle}</h1>
          <p className="preview-subtitle">{t.bookReadySubtitle}</p>
          {orderLink}
        </div>

        <BookViewer pages={pages} font={data.storyFont} />

        {data.includesPdf && (
          <div className="book-ready-download">
            <BookDownload story={data} className="submit-btn book-ready-download-btn" />
            <p className="book-ready-download-hint">{t.bookReadyDownloadHint}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="preview-page">
      <div className="preview-header">
        {lf(template || {}, 'nameAr') && (
          <p className="preview-template">{t.storyLineLabel}: {lf(template, 'nameAr')}</p>
        )}
        <p className="preview-subtitle">{t.storyPreviewSubtitle}</p>
        {orderLink}
      </div>

      <BookViewer pages={pages} font={data.storyFont} editableTitle onTitlePosition={handleTitleMove} onNavigate={handlePreviewNav} />

      <div className="preview-banner">
        <div className="preview-banner-text">
          <p className="preview-banner-title">
            <Sparkles size={18} aria-hidden="true" /> {t.previewBannerTitle}
          </p>
          <p className="preview-banner-sub">{t.previewBannerText}</p>
        </div>
        <Link to={`/checkout/${shareToken}`} className="submit-btn preview-banner-cta" onClick={trackBuyClick}>
          {variant && (
            <span className="preview-banner-price">
              {variant.compareAtCents > variant.priceCents && (
                <span className="price-was">{(variant.compareAtCents / 100).toFixed(0)}{t.currency}</span>
              )}
              {(variant.priceCents / 100).toFixed(0)}{t.currency}
            </span>
          )}
          {t.continueToCheckout}
        </Link>
      </div>

      <section className="preview-assurance">
        <h3 className="preview-assurance-title">
          <Sparkles size={18} aria-hidden="true" /> {t.previewAssuranceTitle}
        </h3>
        <p className="preview-assurance-lead">{t.previewAssuranceLead}</p>
        <ul className="preview-assurance-steps">
          <li><PenLine size={18} aria-hidden="true" /> {t.previewAssuranceStep1}</li>
          <li><Palette size={18} aria-hidden="true" /> {t.previewAssuranceStep2}</li>
          <li><ShieldCheck size={18} aria-hidden="true" /> {t.previewAssuranceStep3}</li>
        </ul>
        <p className="preview-assurance-foot">{t.previewAssuranceFoot}</p>
      </section>

      <div className="preview-cta-section">
        {variant && (
          <p className="preview-price">
            {t[variant.type]} — {variant.compareAtCents > variant.priceCents && (
              <span className="price-was">{(variant.compareAtCents / 100).toFixed(0)}{t.currency}</span>
            )}{(variant.priceCents / 100).toFixed(0)}{t.currency}
          </p>
        )}
        <Link to={`/checkout/${shareToken}`} className="submit-btn preview-cta" onClick={trackBuyClick}>
          {t.continueToCheckout}
        </Link>
        <p className="preview-risk-reversal">
          <ShieldCheck size={15} aria-hidden="true" /> {t.previewRiskReversal}
        </p>
        <div className="trust-bar">
          <span className="trust-bar-item"><ShieldCheck size={16} /> {t.trustPaymentSecure}</span>
          <span className="trust-bar-item"><Star size={16} fill="#f59e0b" color="#f59e0b" /> {STATS.avgRating} {t.trustRatingOf} {STATS.reviewCount} {t.trustRatingReview}</span>
        </div>
      </div>

      <PreviewHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} story={data} shareToken={shareToken} />
    </div>
  );
}
